/**
 * m3 echo Frontend - Billing Tenants Tab
 *
 * Dropdown to select tenant, assignments table below.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Tooltip from '@mui/material/Tooltip';
import {
  BillingOverviewTenant,
  TenantModuleAssignment,
  billingApi,
} from '../../services/billing';
import TenantModuleDialog from './TenantModuleDialog';

const statusColors: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
  aktiv: 'success',
  kulanz: 'warning',
  abgelaufen: 'error',
  geplant: 'info',
};

const statusLabels: Record<string, string> = {
  aktiv: 'Aktiv',
  kulanz: 'Kulanz',
  abgelaufen: 'Abgelaufen',
  geplant: 'Geplant',
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const TenantsTab: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tenants, setTenants] = useState<BillingOverviewTenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [assignments, setAssignments] = useState<TenantModuleAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editAssignment, setEditAssignment] = useState<TenantModuleAssignment | null>(null);

  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await billingApi.getOverview();
      setTenants(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAssignments = useCallback(async (tenantId: number) => {
    try {
      setAssignmentsLoading(true);
      const response = await billingApi.getTenantModules(tenantId);
      setAssignments(response.data);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Zuweisungen');
    } finally {
      setAssignmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // Auto-select first tenant on load
  useEffect(() => {
    if (tenants.length > 0 && selectedTenantId === null) {
      setSelectedTenantId(tenants[0].id);
    }
  }, [tenants, selectedTenantId]);

  useEffect(() => {
    if (selectedTenantId) {
      loadAssignments(selectedTenantId);
    } else {
      setAssignments([]);
    }
  }, [selectedTenantId, loadAssignments]);

  const handleTenantSelect = (tenantId: number) => {
    setSelectedTenantId(tenantId);
    setEditAssignment(null);
  };

  const handleAddAssignment = () => {
    setEditAssignment(null);
    setDialogOpen(true);
  };

  const handleEditAssignment = (assignment: TenantModuleAssignment) => {
    setEditAssignment(assignment);
    setDialogOpen(true);
  };

  const handleDeleteAssignment = async (assignment: TenantModuleAssignment) => {
    if (!window.confirm(`Zuweisung "${assignment.module_display_name}" wirklich entfernen?`)) return;
    try {
      await billingApi.deleteAssignment(assignment.id);
      if (selectedTenantId) loadAssignments(selectedTenantId);
      loadTenants();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditAssignment(null);
  };

  const handleDialogSaved = () => {
    handleDialogClose();
    if (selectedTenantId) loadAssignments(selectedTenantId);
    loadTenants();
  };

  const selectedTenant = tenants.find((t) => t.id === selectedTenantId);

  // Detect overlapping non-quota assignments for the same module
  const overlappingIds = useMemo(() => {
    const ids = new Set<number>();
    // Group non-quota assignments by module_id
    const grouped = new Map<number, TenantModuleAssignment[]>();
    for (const a of assignments) {
      if (a.module_type === 'quota') continue;
      const list = grouped.get(a.module_id) || [];
      list.push(a);
      grouped.set(a.module_id, list);
    }
    for (const group of grouped.values()) {
      if (group.length < 2) continue;
      for (let i = 0; i < group.length; i++) {
        for (let j = i + 1; j < group.length; j++) {
          const a = group[i];
          const b = group[j];
          // Ranges overlap if startA < endB AND startB < endA
          // null active_until means open-ended (use far future)
          const startA = new Date(a.active_from).getTime();
          const endA = a.active_until ? new Date(a.active_until).getTime() : Infinity;
          const startB = new Date(b.active_from).getTime();
          const endB = b.active_until ? new Date(b.active_until).getTime() : Infinity;
          if (startA < endB && startB < endA) {
            ids.add(a.id);
            ids.add(b.id);
          }
        }
      }
    }
    return ids;
  }, [assignments]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const assignmentsTable = (
    <Box>
      {selectedTenant ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddAssignment}
            >
              Modul zuweisen
            </Button>
          </Box>

          {assignmentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Modul</TableCell>
                    <TableCell>Typ</TableCell>
                    {!isMobile && <TableCell>Aktiv von/bis</TableCell>}
                    {!isMobile && <TableCell>Abrechnung</TableCell>}
                    {!isMobile && <TableCell>Rabatt</TableCell>}
                    <TableCell>Preis</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map((a) => (
                    <TableRow
                      key={a.id}
                      sx={overlappingIds.has(a.id) ? { bgcolor: 'warning.light', '& td': { color: 'warning.contrastText' } } : undefined}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {a.module_display_name}
                          </Typography>
                          {overlappingIds.has(a.id) && (
                            <Tooltip title="Doppelbuchung – Zeiträume überschneiden sich">
                              <WarningAmberIcon fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={a.module_type} size="small" variant="outlined" />
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(a.active_from)} – {formatDate(a.active_until)}
                          </Typography>
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(a.billing_from)} – {formatDate(a.billing_until)}
                          </Typography>
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell>
                          {Number(a.discount_percent) > 0 ? `${Number(a.discount_percent)}%` : '-'}
                        </TableCell>
                      )}
                      <TableCell>
                        {a.effective_price.toFixed(2)} €
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabels[a.status] || a.status}
                          color={statusColors[a.status] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEditAssignment(a)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteAssignment(a)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {assignments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          Keine Module zugewiesen
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Mandant auswählen um Module zu verwalten
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        select
        label="Mandant"
        size="small"
        value={selectedTenantId ?? ''}
        onChange={(e) => handleTenantSelect(Number(e.target.value))}
        sx={{ mb: 2, minWidth: 280 }}
      >
        {tenants.map((t) => (
          <MenuItem key={t.id} value={t.id}>
            {t.display_name} — {t.active_module_count} Module · {t.monthly_cost.toFixed(2)} €/Monat
          </MenuItem>
        ))}
      </TextField>

      {assignmentsTable}

      {selectedTenantId && (
        <TenantModuleDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onSaved={handleDialogSaved}
          tenantId={selectedTenantId}
          assignment={editAssignment}
        />
      )}
    </Box>
  );
};

export default TenantsTab;
